import React from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { fetcher } from '@weezevent/weezjs-core';
import {
    Dropdown,
    Table,
    Button,
    WeezHeader,
    DropdownItem,
    Icon,
    Modal,
    FilterSetController,
    Filter,
    Tooltip,
    Card,
    ContentCard,
    Paginator,
    EmptyState,
    toast,
    SelectButton,
    ButtonDropdown,
    InfoText,
    Status,
    Message,
    ToggleContainer,
    Section,
    Drawer,
    DrawerFooter,
    DrawerHeader,
    Loader
} from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Partner from '../../models/partner';
import SortDropdown from '../../components/sortDropdown';
import { TableQuotasCard } from '@weezevent/socium';
import InventoryPartner from '../../models/inventoryPartner';
import * as Sentry from '@sentry/browser';
import Style from './styles/partnerList.module.css';
import LoadingTable from '../../components/loadingTable';
import TemplateSelectionModal from './templateSelectionModal';
import InventorySelectionModal from './InventorySelectionModal';
import ArchiveModal from './archiveModal';
import PartnersReservationModal from './PartnersReservationModal';
import { usePermissions } from '../../user/permissions';
import { useCheckDownload } from '../../components/export/exportButton.js';
import { handleSearchQuery, useCurrentPage, usePageSize } from '../manager/components/context';
import { useNotifications, Notification } from '../../components/notifications/notificationsProvider';

const spec = {
    multiple: ['status__in'],
    fields: ['status__in'],
    form: [],
    aliases: {},
    date: []
};

const initialFilters = { status__in: new Set(['active', 'inactive']) };

const DisplayEventsDrawer = ({ organization, partnerWhoseEventsHaveToBeShown, showDrawer, onclose }) => {
    let history = useHistory();
    const { t } = useTranslation();

    const [stats, loading] = InventoryPartner.useApiModel(
        {
            launch: partnerWhoseEventsHaveToBeShown ? true : false,
            partnerId: partnerWhoseEventsHaveToBeShown?.uuid,
            organizationId: organization.pk,
            onError: e => {
                console.error(e);
                Sentry.captureException(e);
            }
        },
        [organization, partnerWhoseEventsHaveToBeShown]
    );

    return (
        <Drawer
            open={showDrawer}
            onClose={onclose}
            width={400}
            title={<DrawerHeader title={t('ticket.partnersList.table.headers.active_events')} />}
            footer={
                <DrawerFooter
                    actions={[
                        <Button key="close" onClick={onclose}>
                            {t('ticket.common.action.close')}
                        </Button>,
                        <Button
                            key="create_batch"
                            primary
                            onClick={event => {
                                history.push(`/ticket/O${organization.id}/partners/${partnerWhoseEventsHaveToBeShown.uuid}/batches/new`);
                            }}
                        >
                            {t('context_menu.cta_create_batch_booking')}
                        </Button>
                    ]}
                />
            }
        >
            {stats && stats.inventories.length > 0 && <TableQuotasCard datas={stats.inventories}></TableQuotasCard>}
            {stats && stats.inventories.length === 0 && <div>{t('ticket.rates.infos.noInventories')}</div>}
            {loading && <Loader />}
        </Drawer>
    );
};

export const infoStatusDisplay = partner => {
    const { t } = useTranslation();

    if (partner.status === 'active') {
        return { color: 'green', status: 'active', text: t('ticket.common.active') };
    }
    if (partner.status === 'inactive') {
        return { color: 'orange', status: 'inactive', text: t('ticket.common.inactive') };
    }
    if (partner.status === 'deleted') {
        return { color: 'red', status: 'deleted', text: t('ticket.partnersList.partner_status_inactive') };
    }
};

function PartnerStatus({ partner, onClick, setOpenModal, setSelectedPartner, changeStatus }) {
    const status = infoStatusDisplay(partner);
    const { t } = useTranslation();

    const items = React.useMemo(
        () => [
            partner.status === 'inactive' && <DropdownItem key={1} item={t('ticket.common.active')} onClick={() => changeStatus(partner, 'active')} />,
            partner.status === 'active' && (
                <DropdownItem
                    key={2}
                    item={t('ticket.common.inactive')}
                    onClick={() => {
                        setOpenModal(true);
                        setSelectedPartner(partner);
                    }}
                />
            )
        ],
        [partner.status]
    );

    return (
        <div onClick={onClick}>
            {partner.status !== 'deleted' ? (
                <Status.Dropdown items={items} text={status.text} color={status.color} value={status.status} />
            ) : (
                <Status color="red" text={t('ticket.partnersList.partner_status_inactive')} />
            )}
        </div>
    );
}

export function ListDropdownItems({ partners, organization, onError, setOpenReservationModal, exportUrl, setExportUrl, finished, setFinished }) {
    const { t } = useTranslation();

    let history = useHistory();

    const onSubmitAction = React.useCallback(
        process => {
            const url = `/ticket/organizations/${organization.id}/export/`;
            let data = {};
            let payload = {
                status: 'ready',
                process: process,
                data: data
            };
            fetcher
                .post(url, { data: payload })
                .then(({ response, status }) => {
                    if (status !== 201) {
                        throw new Error('Export could not be created');
                    }

                    if (response.status === 'ready' && response.url) {
                        //Launch download here
                        setExportUrl(response.url);
                        setFinished(false);
                        toast.info(t('ticket.export.download_preparation'));
                    }
                })
                .catch(() => {
                    toast.error(t('ticket.export.download_preparation_error'));
                });
        },
        [organization, setExportUrl, setFinished]
    );

    return (
        <>
            <DropdownItem item={t('ticket.partners.settings.button_label')} onClick={() => history.push(`/ticket/O${organization.id}/partners/settings`)} />
            <DropdownItem
                disabled={!finished}
                item={t('ticket.partnersList.export_list_partner')}
                onClick={() => {
                    onSubmitAction('partner');
                }}
            />
            <DropdownItem
                disabled={!finished}
                item={t('ticket.partnersList.export_list_user')}
                onClick={() => {
                    onSubmitAction('partneruser_excel');
                }}
            />
            <DropdownItem
                disabled={!finished}
                item={t('ticket.partnersList.export_reservations_partner.export_title')}
                onClick={() => {
                    setOpenReservationModal(true);
                }}
            />
            <DropdownItem item={t('ticket.partners.imports.title')} onClick={() => history.push(`/ticket/O${organization.id}/partners/imports/new`)} />
        </>
    );
}

const PartnersList = ({ organization }) => {
    let history = useHistory();
    const { addNotification } = useNotifications();

    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = React.useState(null);
    const [selectedPartners, setSelectedPartners] = React.useState({});
    const [ordering, setOrdering] = React.useState('name');
    const [partnerWhoseEventsHaveToBeShown, setPartnerWhoseEventsHaveToBeShown] = React.useState(null);
    const [allPartnersSelected, setAllPartnersSelected] = React.useState(false);
    const [showFreeBookingsModal, setShowFreeBookingsModal] = React.useState(false);
    const [showTemplateModal, setShowTemplateModal] = React.useState(false);
    const permissions = usePermissions();
    const [filters, setFilters] = React.useState(initialFilters);
    const [openModalToInactive, setOpenModalToInactive] = React.useState(false);
    const [selectedPartner, setSelectedPartner] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [freeBookings, setFreeBookings] = React.useState(false);
    const [showArchiveModal, setShowArchiveModal] = React.useState(false);
    const [openReservationModal, setOpenReservationModal] = React.useState(false);
    const [exportUrl, setExportUrl] = React.useState(null);
    const [finished, setFinished] = React.useState(true);

    const [currentPage, setCurrentPage] = useCurrentPage();
    const [pageSize, setPageSize] = usePageSize();

    useCheckDownload(
        exportUrl,
        (error, result) => {
            if (result) {
                setFinished(true);
            }
        },
        () => {
            toast.error(t('ticket.partnersList.download_error'));
        },
        3000
    );

    const [partners, loading, sync] = Partner.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            query: {
                ordering: ordering,
                limit: pageSize,
                offset: currentPage * pageSize,
                search: searchQuery,
                ...handleSearchQuery(spec, filters, false)
            }
        },
        [organization, ordering, searchQuery, pageSize, currentPage, filters]
    );

    const onSelected = React.useCallback(
        ({ event, data: { checked } }, partner) => {
            setSelectedPartners(currentState => {
                const newSelected = { ...currentState };
                const isSelected = partner.uuid in currentState;
                if (!checked && isSelected) {
                    delete newSelected[partner.uuid];
                }

                if (checked && !isSelected) {
                    newSelected[partner.uuid] = partner;
                }
                setAllPartnersSelected(Object.values(newSelected).length === partners.length);

                return newSelected;
            });
        },
        [partners]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            let partnersTmp = {};
            if (checked) {
                partners.forEach(partner => {
                    partnersTmp[partner.uuid] = partner;
                });
            }
            setAllPartnersSelected(checked);
            setSelectedPartners(partnersTmp);
        },
        [partners]
    );

    const changeStatus = React.useCallback(
        (partner, value) => {
            setIsSubmitting(true);

            partner.active = value === 'active';
            partner.freebookings = freeBookings;

            partner
                .save({ organizationId: organization.id })
                .then(() => {
                    toast.success(t('ticket.partnersList.table.toast_status_change.successfull'));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.common.toast_error'));
                })
                .finally(() => {
                    setIsSubmitting(false);
                    setOpenModalToInactive(false);
                    setFreeBookings(false);
                });
        },
        [organization, freeBookings, sync]
    );

    const restorePartner = React.useCallback(
        partner => {
            Partner.perform_action_restore(organization, partner)
                .then(() => {
                    toast.success(t('ticket.partners.restore.success'));
                    sync();
                })
                .catch(error => {
                    console.error(error);
                    toast.error(t('ticket.partners.restore.error'));
                });
        },
        [organization, sync]
    );

    const options = [
        { key: 'active', text: t('ticket.common.active'), name: 'active' },
        { key: 'inactive', text: t('ticket.common.inactive'), name: 'inactive' },
        { key: 'deleted', text: t('ticket.partnersList.partner_status_inactive'), name: 'deleted' }
    ];

    const totalPartners = React.useMemo(() => {
        return Number.parseInt(partners?.__meta?.headers.get('total-count')) || 0;
    }, [partners]);

    const archivablePartnerIds = React.useMemo(() => {
        return Object.values(selectedPartners)
            .filter(partner => partner.status === 'active' || partner.status === 'inactive')
            .map(partner => partner.uuid);
    }, [selectedPartners]);

    const deletedPartnerIds = React.useMemo(() => {
        return Object.values(selectedPartners)
            .filter(partner => partner.status === 'deleted')
            .map(partner => partner.uuid);
    }, [selectedPartners]);

    const hasInactivePartnerInSelection = React.useMemo(() => {
        return Object.values(selectedPartners).some(p => p.status === 'inactive');
    }, [selectedPartners]);

    const hasDeletedPartnerInSelection = React.useMemo(() => {
        return Object.values(selectedPartners).some(p => p.status === 'deleted');
    }, [selectedPartners]);

    const hasActivePartnerInSelection = React.useMemo(() => {
        return Object.values(selectedPartners).some(p => p.status === 'active');
    }, [selectedPartners]);

    const handleRestore = React.useCallback(() => {
        const restorePromise = selectedPartner
            ? selectedPartner.perform_action_restore({ organizationId: organization.id })
            : fetcher.post(`/ticket/organizations/${organization.id}/partners/actions`, {
                  data: { type: 'async_bulk_restore', resource_query_filter: { id__in: deletedPartnerIds.join(',') } }
              });

        restorePromise
            .then(response => {
                if (selectedPartner) {
                    toast.success(t('ticket.partners.restore.success'));
                } else {
                    addNotification(
                        new Notification('async_actions', 'async_bulk_restore', 'partners', response?.response?.task_id, {
                            success: 'ticket.partnersList.restore.toast-success',
                            error: 'ticket.partnersList.restore.toast-error'
                        })
                    );
                    toast.info(t('ticket.manager.attendees.action.generic.toast.async'));
                }
                sync();
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.partners.restore.error'));
            })
            .finally(() => {
                setSelectedPartners({});
                setAllPartnersSelected(false);
                setSelectedPartner(null);
            });
    }, [selectedPartner, organization, sync, addNotification, deletedPartnerIds]);

    return (
        <>
            {openReservationModal && (
                <PartnersReservationModal
                    open={openReservationModal}
                    organization={organization}
                    onClose={() => setOpenReservationModal(false)}
                    setExportUrl={setExportUrl}
                    setFinished={setFinished}
                    finished={finished}
                />
            )}
            <TemplateSelectionModal
                setSelectedPartners={setSelectedPartners}
                showModal={showTemplateModal}
                setShowModal={setShowTemplateModal}
                organizationId={organization.id}
                selectedPartners={selectedPartners}
            />
            <ArchiveModal
                setSelectedPartners={setSelectedPartners}
                showModal={showArchiveModal}
                setShowModal={setShowArchiveModal}
                organizationId={organization.id}
                selectedPartner={selectedPartner}
                partnerIdsToArchive={archivablePartnerIds}
                setAllPartnersSelected={setAllPartnersSelected}
                setSelectedPartner={setSelectedPartner}
                sync={sync}
            />
            <InventorySelectionModal
                organization={organization}
                setAllPartnersSelected={setAllPartnersSelected}
                selectedPartners={selectedPartners}
                setSelectedPartners={setSelectedPartners}
                showModal={showFreeBookingsModal}
                setShowModal={setShowFreeBookingsModal}
            />

            <Modal
                allowExternalClosing={false}
                open={openModalToInactive}
                hideCloseButton={isSubmitting}
                onClose={() => {
                    setOpenModalToInactive(false);
                }}
                submitButton={
                    <Button
                        primary
                        label={t('ticket.common.action.confirm')}
                        onClick={() => {
                            changeStatus(selectedPartner, 'inactive');
                        }}
                        isLoading={isSubmitting}
                        disabled={isSubmitting}
                    />
                }
                cancelButton={
                    <Button
                        onClick={() => {
                            setOpenModalToInactive(false);
                        }}
                        label={t(`ticket.common.action.back`)}
                        disabled={isSubmitting}
                    />
                }
                title={t('ticket.partnersList.modal.to_inactive.title')}
                size="large"
            >
                <div>{t('ticket.partnersList.modal.to_inactive.description')}</div>
                <Message title={t('ticket.partnersList.modal.to_inactive.info_title')} type="default" className={Style['margin-top-small']}>
                    <div>{t('ticket.partnersList.modal.to_inactive.info_message')}</div>
                </Message>
                <Section className={Style['margin-top-small']}>
                    <div className={Style['toggle-container-no-margin']}>
                        <ToggleContainer
                            checked={freeBookings}
                            title={t('ticket.partnersList.modal.to_inactive.toggle_label')}
                            helpLabel={t('ticket.partnersList.modal.to_inactive.toggle_tip')}
                            onChange={() => setFreeBookings(currentState => !currentState)}
                        />
                    </div>
                </Section>
            </Modal>

            <WeezHeader
                title={t('ticket.partnersList.title')}
                rightComponent={
                    <>
                        <Button primary label={t('ticket.partners.new_partner')} onClick={() => history.push(`/ticket/O${organization.id}/partners/new`)} />
                        <ButtonDropdown
                            renderWhenClosed
                            trigger={<Button inverted primary iconPosition="right" icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                            items={[
                                <ListDropdownItems
                                    key="list"
                                    partners={partners}
                                    organization={organization}
                                    onError={() => toast.error(t('ticket.export.download_state_error'))}
                                    setOpenReservationModal={setOpenReservationModal}
                                    exportUrl={exportUrl}
                                    setExportUrl={setExportUrl}
                                    finished={finished}
                                    setFinished={setFinished}
                                />
                            ]}
                        />
                    </>
                }
            >
                {t('ticket.partnersList.header_description')}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <div className={Style['search-box']}>
                        <FilterSetController
                            total={totalPartners}
                            isLoadingTotal={loading}
                            labelTotal={t('ticket.partnersList.subtitle', { count: totalPartners })}
                            labelFilters={t('ticket.common.cta.filters')}
                            initialValues={filters}
                            search={{
                                placeholder: t('ticket.partners.partnerList.search_placeholder'),
                                onChange: (_, value) => setSearchQuery(value),
                                value: searchQuery,
                                handleReset: () => setSearchQuery('')
                            }}
                            onChange={values => {
                                setFilters(values);
                            }}
                        >
                            <Filter
                                index="1"
                                multiple
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelName={t('ticket.accounting.filter.statusLabel')}
                                labelCTA={t('ticket.common.action.apply')}
                                name="status__in"
                                options={options}
                            />
                        </FilterSetController>
                    </div>
                    <DisplayEventsDrawer
                        organization={organization}
                        partnerWhoseEventsHaveToBeShown={partnerWhoseEventsHaveToBeShown}
                        showDrawer={partnerWhoseEventsHaveToBeShown ? true : false}
                        onclose={() => setPartnerWhoseEventsHaveToBeShown(null)}
                    />
                    <div className={`${Style['global-pagesize-container']} ${!permissions.isAdmin() ? Style['global-pagesize-container-hide-button'] : ''}`}>
                        {permissions.isAdmin() && (
                            <SelectButton
                                tooltipTitle={t('ticket.partners.noActionTooltip')}
                                title={
                                    Object.values(selectedPartners).length > 0
                                        ? t('ticket.partners.attendeeAction', { count: Object.values(selectedPartners).length })
                                        : t('ticket.common.action.bulk')
                                }
                                objects={Object.values(selectedPartners)}
                            >
                                {archivablePartnerIds.length > 0 && (
                                    <DropdownItem
                                        disabled={hasDeletedPartnerInSelection}
                                        item={t('ticket.partnersList.freeBookings.cta')}
                                        onClick={() => setShowFreeBookingsModal(true)}
                                    />
                                )}

                                {archivablePartnerIds.length > 0 && (
                                    <DropdownItem disabled={hasDeletedPartnerInSelection} item={t('ticket.partners.batch.edit-email')} onClick={() => setShowTemplateModal(true)} />
                                )}

                                {archivablePartnerIds.length > 0 && (
                                    <DropdownItem
                                        disabled={hasDeletedPartnerInSelection}
                                        item={t('ticket.partnersList.dropdown_delete_partner', { count: archivablePartnerIds.length })}
                                        onClick={() => setShowArchiveModal(true)}
                                    />
                                )}
                                {deletedPartnerIds.length > 0 && (
                                    <DropdownItem
                                        disabled={hasActivePartnerInSelection || hasInactivePartnerInSelection}
                                        item={t('ticket.partnersList.dropdown_restore_partner', { count: deletedPartnerIds.length })}
                                        onClick={handleRestore}
                                    />
                                )}
                            </SelectButton>
                        )}
                    </div>
                </ContentCard>
                <Table useNew selectable onSelectAll={(event, data) => handleAllClick({ event, data })} onSelected>
                    <Table.THead>
                        <Table.Tr active={allPartnersSelected} isHeader>
                            <SortDropdown
                                name="created"
                                ordering={ordering}
                                label={t('ticket.partnersList.table.headers.creation_date')}
                                items={[
                                    <DropdownItem.Icons
                                        key={1}
                                        item={t('ticket.common.ordering.descending')}
                                        checked={ordering === '-created'}
                                        onClick={() => setOrdering('-created')}
                                    />,
                                    <DropdownItem.Icons
                                        key={2}
                                        item={t('ticket.common.ordering.ascending')}
                                        checked={ordering === 'created'}
                                        onClick={() => setOrdering('created')}
                                    />
                                ]}
                            />
                            <SortDropdown
                                className={Style['width-x-large']}
                                name="name"
                                ordering={ordering}
                                label={t('ticket.partnersList.table.headers.name')}
                                items={[
                                    <DropdownItem.Icons key={2} item={t('ticket.common.ordering.Z-A')} checked={ordering === '-name'} onClick={() => setOrdering('-name')} />,
                                    <DropdownItem.Icons key={1} item={t('ticket.common.ordering.A-Z')} checked={ordering === 'name'} onClick={() => setOrdering('name')} />
                                ]}
                            />
                            {/* Temporarily hidden, do not delete, added the dropdown */}
                            {/* <SortDropdown
                                className={Style['width-x-mini']}
                                name="quota"
                                ordering={ordering}
                                label={t('ticket.partnersList.table.headers.quota_taken')}
                                items={[
                                    <DropdownItem.Icons key={2} item={t('ticket.common.ordering.Z-A')} checked={ordering === '-quota'} onClick={() => setOrdering('-quota')} />,
                                    <DropdownItem.Icons key={1} item={t('ticket.common.ordering.A-Z')} checked={ordering === 'quota'} onClick={() => setOrdering('quota')} />
                                ]}
                            /> */}
                            <SortDropdown
                                className={Style['width-x-mini']}
                                name="last_active_date"
                                ordering={ordering}
                                label={t('ticket.partnersList.table.headers.last_activity')}
                                items={[
                                    <DropdownItem.Icons
                                        key={1}
                                        item={t('ticket.common.ordering.descending')}
                                        checked={ordering === '-last_active_date'}
                                        onClick={() => setOrdering('-last_active_date')}
                                    />,
                                    <DropdownItem.Icons
                                        key={2}
                                        item={t('ticket.common.ordering.ascending')}
                                        checked={ordering === 'last_active_date'}
                                        onClick={() => setOrdering('last_active_date')}
                                    />
                                ]}
                            />
                            <th className={Style['width-x-mini']}>{t('ticket.partnersList.table.headers.status')}</th>
                            <th className={Style['width-x-mini']}>
                                <div className={Style['right-most-column']}>{t('ticket.common.cta.actions')}</div>
                            </th>
                        </Table.Tr>
                    </Table.THead>
                    {!loading && partners && (
                        <Table.TBody>
                            {partners?.map(p => (
                                <Table.Tr
                                    active={Boolean(selectedPartners[p.uuid])}
                                    onSelected={(event, data) => onSelected({ event, data }, p)}
                                    key={p.uuid}
                                    onClick={() =>
                                        history.push({
                                            pathname: `/ticket/O${organization.id}/partners/${p.uuid}/dashboard`,
                                            state: { fromPage: currentPage + 1 }
                                        })
                                    }
                                    className={`${p?.isDeleted() ? Style['tr-deleted'] : ''}`}
                                >
                                    <Table.Td>
                                        <InfoText subtitle={Intl.DateTimeFormat(organization.default_locale, { timeStyle: 'short' }).format(new Date(p.created))}>
                                            {Intl.DateTimeFormat().format(new Date(p.created))}
                                        </InfoText>
                                    </Table.Td>

                                    <Table.Td>
                                        <InfoText>{p.name}</InfoText>
                                    </Table.Td>

                                    {/* Temporarily hidden, do not delete */}
                                    {/* <Table.Td>XXX / XXX </Table.Td> */}

                                    <Table.Td>
                                        <InfoText
                                            subtitle={
                                                p.last_active_date
                                                    ? Intl.DateTimeFormat(organization.default_locale, { timeStyle: 'short' }).format(new Date(p.last_active_date))
                                                    : ''
                                            }
                                        >
                                            {p.last_active_date ? Intl.DateTimeFormat().format(new Date(p.last_active_date)) : ''}
                                        </InfoText>
                                    </Table.Td>
                                    <Table.Td>
                                        <PartnerStatus
                                            partner={p}
                                            onClick={e => e.stopPropagation()}
                                            setOpenModal={setOpenModalToInactive}
                                            setSelectedPartner={setSelectedPartner}
                                            changeStatus={changeStatus}
                                        />
                                    </Table.Td>
                                    <Table.Td className={Style['no-padding']}>
                                        <div className={Style['action-icons']}>
                                            <Tooltip margin={false} triggerOnHover={true} title={t('ticket.widget.form.placeholderEventSelector')}>
                                                <Icon name="list" onClick={() => setPartnerWhoseEventsHaveToBeShown(p)}></Icon>
                                            </Tooltip>
                                            <Table.CellDropdown style={Style['dropdown-action']}>
                                                <Dropdown.Item
                                                    item={t('ticket.common.cta.view_attendees')}
                                                    onClick={() => history.push(`/ticket/O${organization.id}/partners/${p.uuid}`)}
                                                />
                                                <Dropdown.Line />
                                                <Dropdown.Item
                                                    disabled={p?.isDeleted()}
                                                    item={t('ticket.common.cta.add_batch')}
                                                    onClick={() => history.push(`/ticket/O${organization.id}/partners/${p.uuid}/batches/new`)}
                                                />
                                                <Dropdown.Item
                                                    disabled={p?.isDeleted()}
                                                    item={t('ticket.partnersList.freeBookings.cta')}
                                                    onClick={() => {
                                                        setSelectedPartners({ [p.uuid]: p });
                                                        setShowFreeBookingsModal(true);
                                                    }}
                                                />
                                                <Dropdown.Item
                                                    disabled={p?.isDeleted()}
                                                    item={t('ticket.common.cta.add_user')}
                                                    onClick={() => history.push(`/ticket/O${organization.id}/partners/${p.uuid}/users`)}
                                                />
                                                <Dropdown.Item
                                                    disabled={p?.isDeleted()}
                                                    item={t('ticket.partnersList.dropdown_edit_partner')}
                                                    onClick={() => history.push(`/ticket/O${organization.id}/partners/${p.uuid}/edit`)}
                                                />
                                                <DropdownItem disabled={p?.isDeleted()} item={t('ticket.partners.batch.edit-email')} onClick={() => setShowTemplateModal(true)} />
                                                <Dropdown.Item
                                                    disabled={!p?.isDeleted()}
                                                    item={t('ticket.partnersList.dropdown_restore_partner')}
                                                    onClick={() => {
                                                        restorePartner(p);
                                                    }}
                                                />
                                                <Dropdown.Line />
                                                <Dropdown.Item
                                                    disabled={p?.isDeleted()}
                                                    item={t('ticket.partnersList.dropdown_delete_partner')}
                                                    onClick={() => {
                                                        setShowArchiveModal(true);
                                                        setSelectedPartner(p);
                                                    }}
                                                />
                                            </Table.CellDropdown>
                                        </div>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={loading} />
                {!loading && !partners?.length && (
                    <EmptyState icon={<Icon name="list" />} title={t('ticket.partnersList.no_partners_title')} subtitle={t('ticket.partnersList.no_partners')} />
                )}
                <Paginator
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    totalCount={+partners?.__meta.headers.get('total-count') || 0}
                    pageSize={pageSize}
                    style={{ padding: 20 }}
                    pageSizes={[50, 100, 200]}
                    setPageSize={setPageSize}
                />
            </Card>
        </>
    );
};

export default PartnersList;
PartnersList.propTypes = {
    organization: PropTypes.object.isRequired
};
